import { canModerateFeed } from '../../../../helpers/feed-moderation';
import FeedBetaProgram from './beta';
import FeedApprovals from './approvals';
import { useState } from 'react';
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { FeedReport, staffFeed } from '../../../../api/feed.api';
import { useAppSelector } from '../../../../hooks';
import { authSelector } from '../../../../store/slices';

function FeedReports() {
  const { user } = useAppSelector(authSelector);
  const [resolved, setResolved] = useState(false);
  const [selected, setSelected] = useState<FeedReport | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const client = useQueryClient();
  const allowed = canModerateFeed(user?.admin);
  const reports = useInfiniteQuery({
    queryKey: ['staff-feed', user?.id, resolved],
    queryFn: ({ pageParam }) => staffFeed.reports(resolved, pageParam),
    getNextPageParam: (page) => page.nextCursor || undefined,
    enabled: allowed && process.env.REACT_APP_FEED_ENABLED === 'true',
  });
  const post = useQuery({
    queryKey: ['staff-feed', user?.id, 'post', selected?.postId],
    queryFn: () => staffFeed.post(selected!.postId),
    enabled: !!selected && allowed,
  });
  const run = async (remove: boolean) => {
    if (!selected || busy) return;
    if (remove && !reason.trim()) {
      setError('Add a removal reason.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      if (remove) await staffFeed.remove(selected, reason);
      await staffFeed.resolve(selected.id);
      setSelected(null);
      setReason('');
      await client.invalidateQueries({ queryKey: ['staff-feed'] });
    } catch {
      setError('The action could not be saved. Please retry.');
    } finally {
      setBusy(false);
    }
  };
  if (!allowed)
    return (
      <p className="p-8">You do not have permission to review feed reports.</p>
    );
  if (process.env.REACT_APP_FEED_ENABLED !== 'true')
    return <p className="p-8">Food feed moderation is disabled.</p>;
  return (
    <section className="p-6 lg:p-10 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold">Food feed reports</h1>
          <p className="text-neutral-500 mt-2">
            Review public posts and private feedback with care.
          </p>
        </div>
        <select
          aria-label="Report status"
          className="border p-3 rounded-lg"
          value={resolved ? 'resolved' : 'open'}
          onChange={(event) => {
            setResolved(event.target.value === 'resolved');
            setSelected(null);
          }}
        >
          <option value="open">Open reports</option>
          <option value="resolved">Resolved reports</option>
        </select>
      </div>
      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          {reports.isLoading && <p>Loading reports…</p>}
          {reports.isError && (
            <button onClick={() => reports.refetch()}>
              Couldn’t load reports. Retry
            </button>
          )}
          {reports.data?.pages
            .flatMap((page) => page.reports)
            .map((report) => (
              <button
                key={report.id}
                className={`w-full text-left p-5 border-b ${
                  selected?.id === report.id ? 'bg-orange-50' : 'bg-white'
                }`}
                onClick={() => {
                  setSelected(report);
                  setReason('');
                  setError('');
                }}
              >
                <p className="font-medium">
                  {report.commentId ? 'Comment' : 'Post'} report
                </p>
                <p className="mt-2 text-sm">{report.reason}</p>
                <p className="text-xs text-neutral-500 mt-3">
                  {new Date(report.createdAt).toLocaleString()}
                </p>
              </button>
            ))}
          {reports.data && !reports.data.pages[0].reports.length && (
            <p className="py-8 text-neutral-500">
              No {resolved ? 'resolved' : 'open'} reports.
            </p>
          )}
          {reports.hasNextPage && (
            <button
              className="p-4 text-orange-700"
              disabled={reports.isFetchingNextPage}
              onClick={() => reports.fetchNextPage()}
            >
              Load more
            </button>
          )}
        </div>
        <div>
          {selected && (
            <article className="border border-neutral-200 bg-white">
              <header className="p-5">
                <p className="font-semibold">
                  {post.data?.author.firstName || 'Content inspection'}
                </p>
                <p className="text-xs text-neutral-500 mt-1">
                  {post.data?.audience === 'PRIVATE'
                    ? 'Private · authorised staff access'
                    : 'Public post'}
                  {post.data?.removed ? ' · Removed' : ''}
                </p>
              </header>
              {post.isLoading && <p className="p-5">Loading content…</p>}
              {post.isError && <p className="p-5">Post unavailable.</p>}
              {post.data?.media.map((media) =>
                media.kind === 'VIDEO' ? (
                  <video
                    key={media.id}
                    controls
                    preload="metadata"
                    poster={media.thumbnailUrl}
                    src={media.url}
                    className="w-full"
                  />
                ) : (
                  <img
                    key={media.id}
                    src={media.url}
                    alt="Reported food post"
                    className="w-full"
                  />
                )
              )}
              <div className="p-5">
                <p className="whitespace-pre-wrap">{post.data?.caption}</p>
                {selected.comment && (
                  <blockquote className="border-l-2 mt-5 pl-4">
                    <p className="text-xs text-neutral-500">
                      Reported comment · {selected.comment.author.firstName}
                    </p>
                    <p className="mt-2 whitespace-pre-wrap">
                      {selected.comment.body}
                    </p>
                  </blockquote>
                )}
                {post.data?.ratings.map((rating, index) => (
                  <p key={index} className="mt-3 text-sm">
                    {rating.brand.name} · {rating.rating}/5
                  </p>
                ))}
                {!resolved && (
                  <>
                    <label
                      className="block mt-6 text-sm font-medium"
                      htmlFor="removal-reason"
                    >
                      Removal reason
                    </label>
                    <textarea
                      id="removal-reason"
                      value={reason}
                      maxLength={500}
                      onChange={(event) => setReason(event.target.value)}
                      className="border w-full mt-2 p-3 min-h-[90px]"
                    />
                    <div className="flex flex-wrap gap-3 mt-4">
                      <button
                        disabled={busy || !post.data || !reason.trim()}
                        onClick={() => run(true)}
                        className="px-4 py-3 bg-orange-600 text-white rounded-lg disabled:opacity-50"
                      >
                        Remove {selected.commentId ? 'comment' : 'post'} and
                        resolve
                      </button>
                      <button
                        disabled={busy}
                        onClick={() => run(false)}
                        className="px-4 py-3 border rounded-lg disabled:opacity-50"
                      >
                        Resolve without removal
                      </button>
                    </div>
                  </>
                )}
                {error && (
                  <p role="alert" className="mt-4 text-red-700">
                    {error}
                  </p>
                )}
              </div>
            </article>
          )}
        </div>
      </div>
    </section>
  );
}

export default function FeedModeration() {
  const [view, setView] = useState<'beta' | 'approvals' | 'reports'>('beta');
  return (
    <>
      <nav
        aria-label="Feed moderation"
        className="flex gap-3 px-6 lg:px-10 pt-6 max-w-6xl mx-auto"
      >
        <button
          aria-pressed={view === 'beta'}
          className="px-4 py-3 border rounded-lg"
          onClick={() => setView('beta')}
        >
          Review beta program
        </button>
        <button
          aria-pressed={view === 'approvals'}
          className="px-4 py-3 border rounded-lg"
          onClick={() => setView('approvals')}
        >
          Awaiting approval
        </button>
        <button
          aria-pressed={view === 'reports'}
          className="px-4 py-3 border rounded-lg"
          onClick={() => setView('reports')}
        >
          User reports
        </button>
      </nav>
      {view === 'beta' ? (
        <FeedBetaProgram />
      ) : view === 'approvals' ? (
        <FeedApprovals />
      ) : (
        <FeedReports />
      )}
    </>
  );
}
